
/*
export class Cache {

    static databases = {};

    static create(dbName, storeName) {
        const key = `${dbName}::${storeName}`;
        if (Cache.databases[key]) return Cache.databases[key];
        const inst = new Cache(dbName, storeName);
        Cache.databases[key] = inst;
        return inst;
    }

    constructor(dbName, storeName) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.db = null;
        this._openPromise = null;

        // Add the databases so they can be deleted later when the user logs out.
        Cache.databases[this.dbName] = this;
    }

    _isQuotaExceeded(error) {
        if (
            error &&
            (error.name === 'QuotaExceededError' ||
                error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || // Firefox
                error.message?.toLowerCase().includes('quota'))
        ) {
            return true;
        }
        return false;
    }

    async deleteDatabase() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
        return new Promise((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(this.dbName);
            deleteRequest.onsuccess = () => resolve(true);
            deleteRequest.onerror = (event) => {
                console.error("Error deleting database", event);
                reject(deleteRequest.error);
            };
            deleteRequest.onblocked = () => {
                reject(new Error("Database deletion blocked by another open connection."));
            };
        });
    }

    _attachDBHandlers(db) {
        db.onversionchange = () => {
            try{ db.close(); } catch {}
            if (this.db === db) this.db = null;
        };
        db.onclose = () => {
            if (this.db === db) this.db = null;
        };
        this.db = db;
    }

    async _ensureDBWithStore(targetVersion=null) {
        if (this.db && this.db.objectStoreNames.contains(this.storeName)) {
            return this.db;
        }
        if (this._openPromise) {
            return this._openPromise;
        }
        this._openPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, targetVersion);

            request.onupgradeneeded = (event) => {
                this.db = event.target.result;
                if (!this.db.objectStoreNames.contains(this.storeName)) {
                    this.db.createObjectStore(this.storeName);
                }
            };

            request.onsuccess = () => {
                const db = request.result;
                const currentVersion = db.version;

                if (db.objectStoreNames.contains(this.storeName)) {
                    this.db = db;
                    resolve(db);
                    this._openPromise = null;
                    return;
                }

                db.close();

                const nextVersion = targetVersion || currentVersion + 1;
                const upgradeRequest = indexedDB.open(this.dbName, nextVersion);

                upgradeRequest.onupgradeneeded = (event) => {
                    const upgradedDB = event.target.result;
                    if (!upgradedDB.objectStoreNames.contains(this.storeName)) {
                        upgradedDB.createObjectStore(this.storeName);
                    }
                };

                upgradeRequest.onblocked = () => {
                    reject(new Error("Database upgrade blocked by another open connection."));
                    this._openPromise = null;
                };

                upgradeRequest.onsuccess = () => {
                    this.db = upgradeRequest.result;
                    resolve(this.db);
                    this._openPromise = null;
                }

                upgradeRequest.onerror = () => {
                    reject(upgradeRequest.error);
                    this._openPromise = null;
                };
            };

            request.onerror = () => {
                reject(request.error);
                this._openPromise = null;
            };
            request.onblocked = () => {
                reject(new Error("Database open blocked by another open connection."));
                this._openPromise = null;
            };
        });

        return this._openPromise;
    }

    async put(key, value) {
        const db = await this._ensureDBWithStore();
        return new Promise((resolve) => {
            const tx = db.transaction(this.storeName, "readwrite");
            tx.onabort = () => resolve(false);
            const store = tx.objectStore(this.storeName);
            const request = store.put(value, key);
            request.onsuccess = () => resolve(true);
            request.onerror = (event) => {
                console.error("Error puttin an element into the cache", event);
                resolve(false);
            };
        });
    }

    async get(key) {
        const db = await this._ensureDBWithStore();
        return new Promise((resolve) => {
            const tx = db.transaction(this.storeName, "readonly");
            tx.onabort = () => resolve(null);
            const store = tx.objectStore(this.storeName);
            const request = store.get(key);

            request.onsuccess = () => {
                return resolve(request.result !== undefined ? request.result : null);
            }
            request.onerror = (event) => {
                console.error("Error getting an element from the cache", event);
                return resolve(null);
            }
        });
    }
}

 */
export class Cache {
    // Key by dbName+storeName to avoid cross-store collisions on the same DB.
    static databases = {};

    static create(dbName, storeName) {
        const key = `${dbName}::${storeName}`;
        if (Cache.databases[key]) return Cache.databases[key];
        const inst = new Cache(dbName, storeName);
        Cache.databases[key] = inst;
        return inst;
    }

    constructor(dbName, storeName) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.db = null;
        this._openPromise = null;
    }

    _isQuotaExceeded(error) {
        return Boolean(
            error &&
            (error.name === 'QuotaExceededError' ||
                error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
                error.message?.toLowerCase().includes('quota'))
        );
    }

    async deleteDatabase() {
        // Close any current connection and clear state
        if (this.db) {
            this.db.close();
            this.db = null;
        }
        this._openPromise = null;

        return new Promise((resolve, reject) => {
            const req = indexedDB.deleteDatabase(this.dbName);
            req.onsuccess = () => resolve(true);
            req.onerror = () => reject(req.error);
            req.onblocked = () =>
                reject(new Error("Database deletion blocked by another open connection."));
        });
    }

    _attachDbHandlers(db) {
        // If someone else upgrades, we must close to avoid blocking.
        db.onversionchange = () => {
            try { db.close(); } catch {}
            if (this.db === db) this.db = null;
        };
        // Optional: handle unexpected close events in some browsers.
        db.onclose = () => {
            if (this.db === db) this.db = null;
        };
        this.db = db;
    }

    async _ensureDBWithStore() {
        if (this.db && this.db.objectStoreNames.contains(this.storeName)) {
            return this.db;
        }
        if (this._openPromise) return this._openPromise;

        this._openPromise = new Promise((resolve, reject) => {
            // 1) Open WITHOUT version to avoid VersionError.
            const openReq = indexedDB.open(this.dbName);

            openReq.onerror = () => {
                this._openPromise = null;
                reject(openReq.error);
            };

            openReq.onblocked = () => {
                this._openPromise = null;
                reject(new Error("Database open blocked by another open connection."));
            };

            openReq.onsuccess = () => {
                const db = openReq.result;
                if (db.objectStoreNames.contains(this.storeName)) {
                    this._attachDbHandlers(db);
                    this._openPromise = null;
                    resolve(db);
                    return;
                }

                // 2) Store missing -> close and upgrade at currentVersion + 1
                const nextVersion = db.version + 1;
                db.close();

                const upgradeReq = indexedDB.open(this.dbName, nextVersion);

                upgradeReq.onupgradeneeded = (event) => {
                    const upgradedDB = event.target.result;
                    if (!upgradedDB.objectStoreNames.contains(this.storeName)) {
                        upgradedDB.createObjectStore(this.storeName);
                    }
                };

                upgradeReq.onerror = () => {
                    this._openPromise = null;
                    reject(upgradeReq.error);
                };

                upgradeReq.onblocked = () => {
                    this._openPromise = null;
                    reject(new Error("Database upgrade blocked by another open connection."));
                };

                upgradeReq.onsuccess = () => {
                    const upgradedDB = upgradeReq.result;
                    this._attachDbHandlers(upgradedDB);
                    this._openPromise = null;
                    resolve(upgradedDB);
                };
            };

            // If DB doesn’t exist yet and a version is NOT supplied, some browsers
            // will still call onupgradeneeded with version 1. Create the store there too.
            openReq.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }
            };
        });

        return this._openPromise;
    }

    async put(key, value) {
        const db = await this._ensureDBWithStore();
        return new Promise((resolve) => {
            const tx = db.transaction(this.storeName, "readwrite");
            tx.onabort = () => resolve(false);
            tx.onerror = () => resolve(false);
            const store = tx.objectStore(this.storeName);
            const req = store.put(value, key);
            req.onsuccess = () => resolve(true);
            req.onerror = () => resolve(false);
        });
    }

    async get(key) {
        const db = await this._ensureDBWithStore();
        return new Promise((resolve) => {
            const tx = db.transaction(this.storeName, "readonly");
            tx.onabort = () => resolve(null);
            tx.onerror = () => resolve(null);
            const store = tx.objectStore(this.storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result !== undefined ? req.result : null);
            req.onerror = () => resolve(null);
        });
    }
}
